import {
  Shield,
  AlertTriangle,
  Bug,
  Zap,
  Activity,
  Cpu,
  Target,
} from "lucide-react";
import type {
  AiInsights,
  RiskLevel,
  RiskLevelCounts,
  InsightSource,
} from "@/lib/api";
import { ScoreGauge } from "./ScoreGauge"; // Used in compact version
import { UpgradeHint } from "./UpgradeHint";
import { calculateAdjustedDistribution } from "@/lib/simplify-results";

interface RiskOverviewProps {
  insights: AiInsights;
  className?: string;
}

const RISK_BAR_CONFIG: Record<
  RiskLevel,
  { label: string; color: string; bgColor: string }
> = {
  critical: {
    label: "Critical",
    color: "bg-red-500",
    bgColor: "bg-red-500/20",
  },
  high: {
    label: "High",
    color: "bg-orange-500",
    bgColor: "bg-orange-500/20",
  },
  medium: {
    label: "Medium",
    color: "bg-amber-500",
    bgColor: "bg-amber-500/20",
  },
  low: {
    label: "Low",
    color: "bg-cyan-500",
    bgColor: "bg-cyan-500/20",
  },
  info: {
    label: "Info",
    color: "bg-slate-500",
    bgColor: "bg-slate-500/20",
  },
};

function RiskDistribution({
  counts,
  total,
}: {
  counts: RiskLevelCounts;
  total: number;
}) {
  const levels: RiskLevel[] = ["critical", "high", "medium", "low", "info"];

  return (
    <div className="space-y-2">
      {levels.map((level) => {
        const count = counts[level];
        const percentage = total > 0 ? (count / total) * 100 : 0;
        const config = RISK_BAR_CONFIG[level];

        return (
          <div key={level} className="group">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                {config.label}
              </span>
              <span className="font-mono font-semibold tabular-nums">
                {count}
              </span>
            </div>
            <div className={`h-2 rounded-full ${config.bgColor} overflow-hidden`}>
              <div
                className={`h-full rounded-full ${config.color} transition-all duration-700 ease-out`}
                style={{ width: `${Math.max(percentage, count > 0 ? 3 : 0)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  color = "cyan",
}: {
  label: string;
  value: number | string;
  icon: React.ElementType;
  color?: "cyan" | "amber" | "red" | "emerald";
}) {
  const colors = {
    cyan: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    red: "text-red-400 bg-red-500/10 border-red-500/20",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border/50 bg-card/30 hover:bg-card/50 transition-colors">
      <div className={`p-2 rounded-lg ${colors[color]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <div className="text-lg font-mono font-bold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function SourceBadge({ source }: { source: InsightSource }) {
  const isML = source === "local_ml";

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
        text-xs font-medium uppercase tracking-wider
        ${
          isML
            ? "bg-purple-500/10 text-purple-400 border border-purple-500/30"
            : "bg-slate-500/10 text-slate-400 border border-slate-500/30"
        }
      `}
    >
      {isML ? <Cpu className="h-3 w-3" /> : <Target className="h-3 w-3" />}
      {isML ? "ML-Enhanced" : "Rule-Based"}
    </span>
  );
}

function getRiskLevelFromScore(score: number): RiskLevel {
  if (score >= 8) return "critical";
  if (score >= 6) return "high";
  if (score >= 4) return "medium";
  if (score >= 2) return "low";
  return "info";
}

const RISK_LEVEL_CONFIG: Record<RiskLevel, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  glowColor: string;
}> = {
  critical: {
    label: "CRITICAL",
    color: "text-red-400",
    bgColor: "bg-red-500/15",
    borderColor: "border-red-500/40",
    glowColor: "shadow-[0_0_30px_rgba(239,68,68,0.3)]",
  },
  high: {
    label: "HIGH",
    color: "text-orange-400",
    bgColor: "bg-orange-500/15",
    borderColor: "border-orange-500/40",
    glowColor: "shadow-[0_0_30px_rgba(249,115,22,0.3)]",
  },
  medium: {
    label: "MEDIUM",
    color: "text-amber-400",
    bgColor: "bg-amber-500/15",
    borderColor: "border-amber-500/40",
    glowColor: "shadow-[0_0_30px_rgba(245,158,11,0.25)]",
  },
  low: {
    label: "LOW",
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/15",
    borderColor: "border-cyan-500/40",
    glowColor: "shadow-[0_0_30px_rgba(34,211,238,0.2)]",
  },
  info: {
    label: "INFO",
    color: "text-slate-400",
    bgColor: "bg-slate-500/15",
    borderColor: "border-slate-500/40",
    glowColor: "",
  },
};

function RiskLevelBadge({ level, urgentCount }: { level: RiskLevel; urgentCount: number }) {
  const config = RISK_LEVEL_CONFIG[level];

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Main risk level badge */}
      <div
        className={`
          relative flex items-center justify-center
          w-32 h-32 rounded-full
          ${config.bgColor} ${config.borderColor} border-2
          ${config.glowColor}
          transition-all duration-500
        `}
      >
        {/* Animated ring for high/critical */}
        {(level === "critical" || level === "high") && (
          <div
            className={`
              absolute inset-0 rounded-full border-2 ${config.borderColor}
              animate-ping opacity-30
            `}
          />
        )}
        <span className={`text-3xl font-bold tracking-tight ${config.color}`}>
          {config.label}
        </span>
      </div>

      {/* Urgent issues badge */}
      {urgentCount > 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20">
          <AlertTriangle className="h-3.5 w-3.5 text-red-400" />
          <span className="text-xs font-medium text-red-400">
            {urgentCount} urgent {urgentCount === 1 ? "issue" : "issues"}
          </span>
        </div>
      )}
    </div>
  );
}

export function RiskOverview({ insights, className = "" }: RiskOverviewProps) {
  const { summary, source, upgrade_hint, findings } = insights;

  // Calculate adjusted distribution for consistency with Simple view
  const adjustedDistribution = calculateAdjustedDistribution(findings);

  // Determine risk level from adjusted distribution
  const getAdjustedRiskLevel = (): RiskLevel => {
    if (adjustedDistribution.critical > 0) return "critical";
    if (adjustedDistribution.high > 0) return "high";
    if (adjustedDistribution.medium > 0) return "medium";
    if (adjustedDistribution.low > 0) return "low";
    return "info";
  };
  const maxRiskLevel = getAdjustedRiskLevel();

  // Count critical + high as "urgent" (using adjusted distribution)
  const urgentCount = adjustedDistribution.critical + adjustedDistribution.high;

  return (
    <div
      className={`
        relative rounded-xl border border-border bg-card/50 overflow-hidden
        ${className}
      `}
    >
      {/* Decorative gradient header */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-amber-500 to-red-500 opacity-60" />

      {/* Scan line effect */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent animate-pulse" />
      </div>

      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <Shield className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">Risk Overview</h3>
              <p className="text-xs text-muted-foreground">
                AI-powered security analysis
              </p>
            </div>
          </div>
          <SourceBadge source={source} />
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Risk Level indicator - clear and unambiguous */}
          <div className="flex flex-col items-center justify-center p-4 rounded-lg bg-muted/20 border border-border/30">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3">
              Overall Risk Level
            </div>
            <RiskLevelBadge level={maxRiskLevel} urgentCount={urgentCount} />
          </div>

          {/* Risk distribution (with adjusted severities) */}
          <div className="p-4 rounded-lg bg-muted/20 border border-border/30">
            <div className="text-xs text-muted-foreground uppercase tracking-wider mb-4">
              Risk Distribution
            </div>
            <RiskDistribution
              counts={adjustedDistribution}
              total={summary.total_findings}
            />
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              label="Total Findings"
              value={summary.total_findings}
              icon={Activity}
              color="cyan"
            />
            <StatCard
              label="CVE Issues"
              value={summary.cve_findings}
              icon={Bug}
              color={summary.cve_findings > 0 ? "amber" : "cyan"}
            />
            <StatCard
              label="EPSS Enriched"
              value={summary.epss_enriched}
              icon={Zap}
              color="emerald"
            />
            <StatCard
              label="KEV Listed"
              value={summary.kev_listed}
              icon={AlertTriangle}
              color={summary.kev_listed > 0 ? "red" : "cyan"}
            />
          </div>
        </div>

        {/* Additional stats row */}
        <div className="mt-6 pt-4 border-t border-border/50 flex items-center justify-between text-sm">
          <div className="flex items-center gap-6 text-muted-foreground">
            <span>
              Avg Score:{" "}
              <span className="font-mono font-semibold text-foreground">
                {summary.avg_score.toFixed(1)}
              </span>
            </span>
            <span>
              Max Score:{" "}
              <span className="font-mono font-semibold text-foreground">
                {summary.max_score.toFixed(1)}
              </span>
            </span>
            <span>
              Modules with findings:{" "}
              <span className="font-mono font-semibold text-foreground">
                {Object.keys(summary.extraction.by_module).length}
              </span>
            </span>
          </div>
        </div>

        {/* Upgrade hint */}
        {upgrade_hint && (
          <div className="mt-4">
            <UpgradeHint hint={upgrade_hint} variant="inline" />
          </div>
        )}
      </div>
    </div>
  );
}

// Compact version for dashboard
export function RiskOverviewCompact({
  insights,
  target,
  className = "",
}: {
  insights: AiInsights;
  target: string;
  className?: string;
}) {
  const { summary } = insights;
  const maxRiskLevel = getRiskLevelFromScore(summary.max_score);
  const urgentCount =
    summary.by_risk_level.critical + summary.by_risk_level.high;

  return (
    <div
      className={`
        rounded-lg border border-border bg-card/50 p-4
        hover:border-cyan-500/30 transition-colors
        ${className}
      `}
    >
      <div className="flex items-center gap-4">
        <ScoreGauge
          score={summary.max_score}
          riskLevel={maxRiskLevel}
          size="sm"
          showLabel={false}
        />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-sm font-medium truncate">{target}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {summary.total_findings} findings
            {urgentCount > 0 && (
              <span className="text-red-400 ml-2">
                ({urgentCount} urgent)
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RiskOverview;
